import React, { useEffect, useState } from "react";
import { Save, RotateCcw, FileText, ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { invalidateLegalDocCache } from "../legal/HoldHarmless";

const KEYS = [
    { key: "public_disclaimer",   label: "Public responsibility disclaimer", note: "Shown on /about under Governance and as the source of truth across the site." },
    { key: "volunteer_agreement", label: "Volunteer agreement",              note: "Read-and-acknowledge text inside the volunteer signup form." },
    { key: "contractor_agreement", label: "Contractor agreement",            note: "Read-and-acknowledge text inside the contractor application." },
    { key: "contractor_insurance", label: "Contractor insurance requirement", note: "Insurance / qualifications notice — typically one paragraph." },
];

const STYLES = [
    { value: "p", label: "Paragraph" },
    { value: "small", label: "Small" },
    { value: "italic", label: "Italic note" },
];

const blankParagraph = () => ({ text: "", style: "p" });

const LegalDocsPanel = () => {
    const [active, setActive] = useState(KEYS[0].key);
    const [docs, setDocs] = useState({});
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState(null); // { title, paragraphs }
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/legal-docs");
            setDocs(data || {});
        } catch (e) {
            toast.error("Could not load legal documents");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        const d = docs[active];
        if (!d) { setDraft(null); return; }
        // Deep-clone so the form is editable without mutating the cached doc
        setDraft({ title: d.title, paragraphs: d.paragraphs.map((p) => ({ ...p })) });
    }, [active, docs]);

    const update = (mutator) => setDraft((curr) => mutator({ ...curr, paragraphs: curr.paragraphs.map((p) => ({ ...p })) }));

    const save = async () => {
        if (!draft) return;
        if (!draft.title.trim() || draft.paragraphs.length === 0 || draft.paragraphs.some((p) => !p.text.trim())) {
            toast.error("Title and every paragraph must be non-empty.");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/admin/legal-docs/${active}`, draft);
            invalidateLegalDocCache(active);
            toast.success("Legal document saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const restore = async () => {
        if (!window.confirm("Restore this document to its original seeded text? Your current edits will be replaced.")) return;
        try {
            await api.post(`/admin/legal-docs/${active}/restore`);
            invalidateLegalDocCache(active);
            toast.success("Restored to defaults");
            await load();
        } catch (e) {
            toast.error("Could not restore");
        }
    };

    const movePara = (idx, delta) => {
        const j = idx + delta;
        if (j < 0 || !draft || j >= draft.paragraphs.length) return;
        update((d) => {
            [d.paragraphs[idx], d.paragraphs[j]] = [d.paragraphs[j], d.paragraphs[idx]];
            return d;
        });
    };

    const addPara = () => update((d) => ({ ...d, paragraphs: [...d.paragraphs, blankParagraph()] }));
    const removePara = (idx) => update((d) => ({ ...d, paragraphs: d.paragraphs.filter((_, i) => i !== idx) }));
    const setPara = (idx, patch) => update((d) => {
        d.paragraphs[idx] = { ...d.paragraphs[idx], ...patch };
        return d;
    });

    const meta = docs[active];

    return (
        <div className="space-y-6" data-testid="legal-docs-panel">
            <div>
                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#E53935]" strokeWidth={1.5} /> Legal documents
                </h2>
                <p className="text-sm text-[#4A5568] mt-1">
                    Edit the canonical legal copy used across the site (volunteer form, contractor application, and About page). Every change is auditable via `updated_at` / `updated_by` and you can <strong>Restore defaults</strong> at any time. Body supports <code>**bold**</code>.
                </p>
            </div>

            <div className="flex flex-wrap gap-2">
                {KEYS.map((k) => (
                    <button
                        key={k.key}
                        onClick={() => setActive(k.key)}
                        data-testid={`legal-doc-tab-${k.key}`}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition border ${active === k.key ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white text-[#1C252E] border-black/10 hover:bg-[#F4F1EA]"}`}
                    >
                        {k.label}
                    </button>
                ))}
            </div>

            {loading || !draft ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : (
                <div className="bg-white border border-black/10 rounded-2xl p-6 space-y-4" data-testid="legal-doc-editor">
                    <p className="text-xs text-[#4A5568] italic">{KEYS.find((k) => k.key === active).note}</p>

                    <div>
                        <label htmlFor="ldoc-title" className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Title</label>
                        <input
                            id="ldoc-title"
                            type="text"
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            maxLength={200}
                            data-testid="legal-doc-title-input"
                            className="mt-1 w-full rounded-xl border border-black/10 px-4 py-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935]"
                        />
                    </div>

                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568] mb-2">Paragraphs ({draft.paragraphs.length})</p>
                        <ol className="space-y-3">
                            {draft.paragraphs.map((p, i) => (
                                <li key={i} className="bg-[#F4F1EA]/50 rounded-xl p-3 space-y-2" data-testid={`legal-doc-para-${i}`}>
                                    <div className="flex items-center justify-between gap-2 flex-wrap">
                                        <span className="text-xs font-bold text-[#1C252E]">¶ {i + 1}</span>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={p.style}
                                                onChange={(e) => setPara(i, { style: e.target.value })}
                                                data-testid={`legal-doc-para-style-${i}`}
                                                className="text-xs rounded-full bg-white border border-black/10 px-3 py-1"
                                            >
                                                {STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                                            </select>
                                            <button onClick={() => movePara(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`legal-doc-para-up-${i}`} className="p-1.5 text-[#4A5568] rounded-full hover:bg-white disabled:opacity-30">
                                                <ArrowUp className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                            <button onClick={() => movePara(i, +1)} disabled={i === draft.paragraphs.length - 1} aria-label="Move down" data-testid={`legal-doc-para-down-${i}`} className="p-1.5 text-[#4A5568] rounded-full hover:bg-white disabled:opacity-30">
                                                <ArrowDown className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                            <button onClick={() => removePara(i)} disabled={draft.paragraphs.length === 1} aria-label="Remove" data-testid={`legal-doc-para-remove-${i}`} className="p-1.5 text-[#4A5568] rounded-full hover:text-[#E53935] hover:bg-[#E53935]/10 disabled:opacity-30">
                                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </div>
                                    </div>
                                    <textarea
                                        rows={4}
                                        value={p.text}
                                        onChange={(e) => setPara(i, { text: e.target.value })}
                                        maxLength={4000}
                                        data-testid={`legal-doc-para-text-${i}`}
                                        className="w-full rounded-lg border border-black/10 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#E53935]/40 focus:border-[#E53935] text-sm"
                                    />
                                </li>
                            ))}
                        </ol>
                        <button
                            type="button"
                            onClick={addPara}
                            disabled={draft.paragraphs.length >= 20}
                            data-testid="legal-doc-add-para-btn"
                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#E53935] hover:underline disabled:opacity-50"
                        >
                            <Plus className="w-4 h-4" strokeWidth={2} /> Add paragraph
                        </button>
                    </div>

                    <div className="flex items-center justify-between pt-3 border-t border-black/5 flex-wrap gap-2">
                        <p className="text-xs text-[#4A5568]">
                            {meta?.updated_at
                                ? <>Last saved {new Date(meta.updated_at).toLocaleString()} by <span className="font-medium">{meta.updated_by}</span></>
                                : <>Never edited (seed text)</>}
                        </p>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={restore}
                                data-testid="legal-doc-restore-btn"
                                className="inline-flex items-center gap-1 text-sm font-medium text-[#4A5568] hover:text-[#1C252E]"
                            >
                                <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Restore defaults
                            </button>
                            <button
                                onClick={save}
                                disabled={saving}
                                data-testid="legal-doc-save-btn"
                                className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2 text-sm font-medium disabled:opacity-60"
                            >
                                <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Saving…" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LegalDocsPanel;
